import { ThemeEnum, EquipmentRead, SwitchingGroupedCols } from '@gridcon-energy/schaltungstool-backend'
import type {
  SwitchingCategoryTypeEnum,
  SwitchingPremiseViolationStaticEnum,
  UserRead,
  EquipmentTypeEnum,
  Entities,
  ServicesHealthCheck,
} from '@gridcon-energy/schaltungstool-backend'
import { de } from 'vuetify/locale'
import sina from './de.sina'
import type { DataTypesEnum } from '~/types/helpers'
import {
  PremiseViolationDynamicEnum,
  ResolutionEnum,
  ResolutionModeEnum,
  StartEndPresetEnum,
  RowLabelConfiguration,
  GroupingPresetEnum,
  BarTypesEnum,
} from '~/lib/gantt'

import { SwitchingColoringModeEnum, SVGLineStyleEnum } from '~/composables/coloring/constants'
import { FileDataFormatEnum, SwitchingEventsEnum } from '~/lib/activity'
import { PageOrientation, PreviewProgressEnum, ReportSectionTypeEnum, ZoomLevelEnum } from '~/composables/report/utils'
import type { NetworkTitleBlockPage } from '~/composables/report/utils'
import type { EquipmentColumnTitles } from '~/composables/tables/useEquipmentColumns'
import { NetworkStateColoringEnum } from '~/composables/coloring/useSVGNetworkStateColoring'
import { PageMargins } from '~/lib/services/export/gotenberg'
import { OfflineStatusEnum, SwitchingEqualCheckPaths } from '~/lib/switchings'
import { EquipmentNetworkStateEnum, EquipmentStatusEnum, NetworkDisplayModeEnum } from '~/lib/equipments'
import { UnlockBarsScopeEnum } from '~/composables/gantt/useSwitchingsLocked'
import type { TableColumnEnumType } from '~/lib/gantt/columnTable'
import { ColumnComputedEnum } from '~/lib/gantt/columnComputed'
import type { TopologyChangesGroupingEnum } from '~/lib/topologies'

const Theme: { [key in ThemeEnum]: string } = {
  [ThemeEnum.Light]: 'hell',
  [ThemeEnum.Dark]: 'dunkel',
  [ThemeEnum.Auto]: 'automatisch',
}

const ImportEntities: { [key in keyof Entities]: string } = {
  equipments: 'Betriebsmittel',
  sub_projects: 'Teilprojekte',
  switchings: 'Schaltungen',
  switching_blocks: 'Schaltungsblöcke',
  switching_block_restrictions: 'Schaltungsblock-Prämissen',
  projects: 'Projekte',
}

const ServiceHealthChecks: { [key in keyof ServicesHealthCheck]: string } = {
  database: 'HAnS Datenbank',
  database_sina: 'SINA Datenbank',
  redis: 'Worker Broker (Redis)',
  celery_worker: 'Worker Service (Celery)',
  gotenberg: 'PDF-Service (Gotenberg)',
  saml_idp_metadata: 'SSO-Auth (SAML Metadata)',
  saml_configuration: 'SSO-Auth (SAML Configuration)',
  file_storage: 'Remote-Filesystem (S3/SMB)',
}

const NetworkDisplayMode: { [key in NetworkDisplayModeEnum]: string } = {
  [NetworkDisplayModeEnum.start]: 'Startnetz',
  [NetworkDisplayModeEnum.end]: 'Zielnetz',
  [NetworkDisplayModeEnum.topology]: 'Topologien',
  [NetworkDisplayModeEnum.equipment_status]: 'Betriebsmittel Status',
}
const NetworkDisplayModeStateHint: {
  [key in NetworkDisplayModeEnum.start | NetworkDisplayModeEnum.end]: string
} = {
  [NetworkDisplayModeEnum.start]:
    'Betriebsmittelzustand am Beginn des Schaltungskalenders vor Berücksichtigung aller Schaltungen',
  [NetworkDisplayModeEnum.end]:
    'Betriebsmittelzustand am Ende des Schaltungskalenders nach Berücksichtigung aller Schaltungen',
}

const EquipmentNetworkState: { [key in EquipmentNetworkStateEnum]: string } = {
  [EquipmentNetworkStateEnum.ONLINE]:
    'Betriebsmittel aktiv - nicht geschaltet aber durch Schaltungskalender beeinflusst | Betriebsmittel aktiv',
  [EquipmentNetworkStateEnum.OFFLINE]: 'Betriebsmittel inaktiv - durch IBN/ABN geschaltet | Betriebsmittel inaktiv',
}

const NetworkStateColoring: { [key in NetworkStateColoringEnum]: string } = {
  [NetworkStateColoringEnum.HIDDEN]: 'Inaktive Betriebsmittel ausblenden',
  [NetworkStateColoringEnum.COLORED]: 'Einfärben',
}
const SVGLineStyle: { [key in SVGLineStyleEnum]: string } = {
  [SVGLineStyleEnum.BORDER]: 'Umrandung',
  [SVGLineStyleEnum.GLOW]: 'Leuchten',
  [SVGLineStyleEnum.FILL]: 'Füllen',
}

const OfflineRangeType: { [key in OfflineStatusEnum]: string } = {
  [OfflineStatusEnum.temporary]: 'Temporär nicht verfügbar',
  [OfflineStatusEnum.pendingActivation]: 'Geht durch IBN in Betrieb',
  [OfflineStatusEnum.deactivated]: 'Ist durch ABN außer Betrieb genommen',
}

const ColoringMode: { [key in SwitchingColoringModeEnum]: string } = {
  [SwitchingColoringModeEnum.CATEGORY]: 'Färbung nach Schaltungskategorie',
  [SwitchingColoringModeEnum.PROJECT]: 'Färbung nach Projekt',
  [SwitchingColoringModeEnum.VOLTAGE]: 'Färbung nach Spannung',
  [SwitchingColoringModeEnum.EQUIPMENT_TYPE]: 'Färbung nach Betriebsmitteltyp',
}

const ZoomLevel: { [key in ZoomLevelEnum]: string } = {
  [ZoomLevelEnum.one]: '100%',
  [ZoomLevelEnum.two]: '50%',
  [ZoomLevelEnum.three]: '33%',
  [ZoomLevelEnum.four]: '25%',
}

const ReportSectionType: { [key in ReportSectionTypeEnum]: string } = {
  [ReportSectionTypeEnum.FRONT_MATTER]: 'Deckblatt & Inhaltsverzeichnis',
  [ReportSectionTypeEnum.GANTT_CHART]: 'Schaltungsdiagramm',
  [ReportSectionTypeEnum.GANTT_COVER]: 'Schaltungsdiagramm - Deckblatt',
  [ReportSectionTypeEnum.SWITCHINGS_TABLE]: 'Schaltvorgänge',
  [ReportSectionTypeEnum.EQUIPMENTS_TABLE]: 'Betriebsmittel',
  [ReportSectionTypeEnum.PROJECTS_TABLE]: 'Projekte',
  [ReportSectionTypeEnum.SUBPROJECTS_TABLE]: 'Teilprojekte',
  [ReportSectionTypeEnum.TOPOLOGY_MATRIX]: 'Schaltungsmatrix',
  [ReportSectionTypeEnum.TOPOLOGY_TABLE]: 'Topologien',
  [ReportSectionTypeEnum.NETWORK_TOPOLOGIES]: 'Netzdiagramm - Topologien',
  [ReportSectionTypeEnum.NETWORK_STATE_START]: 'Netzdiagramm - Startnetz',
  [ReportSectionTypeEnum.NETWORK_STATE_END]: 'Netzdiagramm - Zielnetz',
  [ReportSectionTypeEnum.NETWORK_EQUIPMENT_STATUS]: 'Netzdiagramm - Betriebsmittel Status',
  [ReportSectionTypeEnum.CUSTOM]: 'Benutzerdefinierter Abschnitt',
}

const PreviewProgress: { [key in PreviewProgressEnum]: string } = {
  [PreviewProgressEnum.COMPONENT]: 'Rendere Komponente ',
  [PreviewProgressEnum.PDF]: 'Erstelle PDF (1/2)',
  [PreviewProgressEnum.SPLIT]: 'Erstelle PDF (2/2)',
  [PreviewProgressEnum.IMAGES]: 'Erstelle Vorschaubilder aus PDF',
  [PreviewProgressEnum.PAGES]: 'Erstelle Seiten',
}

const PageOrientations: { [key in PageOrientation]: string } = {
  [PageOrientation.PORTRAIT]: 'Hochformat | hoch',
  [PageOrientation.LANDSCAPE]: 'Querformat | quer',
}
const PageMarginsMap: { [key in keyof PageMargins]: string } = {
  top: 'Oben',
  bottom: 'Unten',
  left: 'Links',
  right: 'Rechts',
}

const NetworkTitleBlock: { [key in keyof NetworkTitleBlockPage]: string } = {
  _type: 'internal',
  logo_of_owner: 'Inhaber (Logo)',
  created_by: 'Erstellt durch',
  approved_by: 'Geprüft von',
  document_type: 'Dokumentart',
  title: 'Titel',
  project: 'Projekt',
  item_number: 'Sachnummer',
  version: 'Version',
  date: 'Datum',
  page_size: 'Blattgröße',
  draft: 'Entwurf',
  page_number: 'Blatt',
  sub_title: 'Untertitel',
}

const import_file_id = 'Import-Datei Id'

const EquipmentColumns: { [key in keyof Required<EquipmentRead>]: string } = {
  id: 'Betriebsmittel (ID)',
  name: 'Betriebsmittel',
  description: 'Beschreibung',
  type: 'Typ',
  voltage_level: "Spannung {'|'} kV | kV",
  integral_name: 'Integral Name',
  integral_key: 'Integral Schlüssel',
  integral_foreign_key: 'Integral Fremdschlüssel',
  external_equipment_id: 'Externe Betriebsmittel-ID',
  activity_id: 'Schaltungskalender-ID',
  import_file_id: import_file_id,
}

const SwitchingType: { [key in SwitchingCategoryTypeEnum]: string } = {
  deactivated: 'Außerbetriebnahme | ABN',
  activated: 'Inbetriebnahme | IBN',
  disabled: 'Freischaltung | FS',
  protected: 'Schutzschaltung | SS',
  bypassMode: 'US-Betrieb | US-B',
  meta: 'Meta Info | Meta',
}

const EquipmentColumnsExtra: { [key in EquipmentColumnTitles]: string } = {
  ...EquipmentColumns,
  status: 'Status',
  inSVG: 'Im Netzdiagramm vorhanden',
  hasSwitchings: 'Im Schaltungsdiagramm vorhanden',
  switchingIntegrity: 'Schaltungs-Integrität',
  first: 'Erste:',
  first_activated: SwitchingType.activated,
  first_bypassMode: SwitchingType.bypassMode,
  first_deactivated: SwitchingType.deactivated,
  first_disabled: SwitchingType.disabled,
  first_meta: SwitchingType.meta,
  first_protected: SwitchingType.protected,
  last: 'Letzte:',
  last_activated: SwitchingType.activated,
  last_bypassMode: SwitchingType.bypassMode,
  last_deactivated: SwitchingType.deactivated,
  last_disabled: SwitchingType.disabled,
  last_meta: SwitchingType.meta,
  last_protected: SwitchingType.protected,
  count: 'Anzahl:',
  count_activated: SwitchingType.activated,
  count_bypassMode: SwitchingType.bypassMode,
  count_deactivated: SwitchingType.deactivated,
  count_disabled: SwitchingType.disabled,
  count_meta: SwitchingType.meta,
  count_protected: SwitchingType.protected,
}

/**
 * We hack pluralization Feature to create shorthands/abbreviations
 * t('val') -> long
 * t('val', 0) -> short
 */
const TableCols = {
  project: {
    id: 'Projekt',
    name: 'Projekt',
    description: 'Beschreibung',
    grid_operator: 'Netzbetreiber',
    internal_id: 'Interne ID',
    project_type: 'Projekttyp',
    user_id: 'Benutzer-ID',
    import_file_id: 'Importdatei-ID',
    activity_id: 'Schaltungsplan-ID',
  },
  sub_project: {
    id: 'Teilprojekt',
    name: 'Teilprojekt',
    artificial: 'künstlich',
    project_id: 'Projekt-ID',
    import_file_id: import_file_id,
  },
  equipment: EquipmentColumns,
  switching: {
    id: 'Schaltungs-ID',
    start: "Startdatum {'|'} Start | Start",
    end: "Enddatum {'|'} Ende | Ende",
    start_initial: 'Start initial',
    end_initial: 'Ende initial',
    duration_days: 'Dauer in Tage',
    duration_release_hours: "Rückschaltzeit in h {'|'} RSZ. | RSZ.",
    db_import: 'DB-Import',
    notes: 'Notiz',
    comment: 'Kommentar',
    switching_category_type: 'Schaltungskategorie',
    switching_category_type_description: 'Beschreibung des Schaltkategorie-Typs',
    equipment_id: 'Betriebsmittel-ID',
    sub_project_id: 'Teilprojekt-ID',
    switching_block_id: 'Schaltungsblock-ID',
    import_file_id: import_file_id,
  },
  switching_block: {
    id: 'Schaltungsblock-ID',
    name: 'Schaltungsblock',
    description: 'Beschreibung',
    artificial: 'Künstlich',
    start_earliest: "Frühester Start {'|'} frühestens  | frühestens ",
    end_latest: " Spätestes Ende {'|'} spätestens | spätestens",
    import_file_id: 'Importdatei',
    activity_id: 'Schaltungskalender-ID',
  },
  switching_info_tbt: {
    continuous: " durchgehend {'|'} Dgh. | Dgh.",
    daily: " täglich {'|'} Tgl. | Tgl.",
    workday: " werktags {'|'} werkt. | werkt.",
    saturday: " samstags {'|'} Sa | Sa",
    sunday: " sonntags {'|'} So | So",
    alternating: " wechselweise {'|'} w.w. | w.w.",
    id: 'ID',
    switching_id: 'Schaltungs-ID',
  },
} satisfies {
  [Table in keyof SwitchingGroupedCols]: Record<TableColumnEnumType<Table>, string>
}

const SwitchingsTimeAxisLabel: {
  [key in keyof Required<RowLabelConfiguration>]: string
} = {
  SubProject: 'Phase/Teilprojekt',
  Project: 'Projekt',
  Equipment: 'Betriebsmittel',
}
const GroupingPresetOption: { [key in GroupingPresetEnum]: string } = {
  [GroupingPresetEnum.Without]: 'ohne Gliederung',
  [GroupingPresetEnum.Equipment]: 'nach Betriebsmittel gegliedert',
  [GroupingPresetEnum.EquipmentProject]: 'nach Projekt und Betriebsmittel gegliedert',
  [GroupingPresetEnum.EquipmentProjectSubProject]: 'nach Projekt, Teilprojekt und Betriebsmittel gegliedert',
}
const EquipmentTypes: { [key in EquipmentTypeEnum]: string } = {
  transformator: 'Transformator - Trafo',
  power_plant: 'Kraftwerk',
  power_line: 'Allgemeiner Stromkreis',
  overhead_power_line: 'Freileitung',
  underground_power_line: 'Erdkabel',
  busbar: 'Sammelschiene',
  connector_socket: 'Kupplung',
  compensation_system: 'Kompensationsanlage',
  field: 'Feld',
  misc: 'Unbekanntes Betriebsmittel',
}

const EquipmentStatus: { [key in EquipmentStatusEnum]: string } = {
  [EquipmentStatusEnum.EXISTING]: 'Bestand',
  [EquipmentStatusEnum.ADDITION]: 'Zubau',
  [EquipmentStatusEnum.DISMANTLING]: 'Abbau',
  [EquipmentStatusEnum.TEMP_AVAILABLE]: 'Temporär verfügbar',
  // [EquipmentStatusEnum.TEMP_NOT_AVAILABLE]: 'Temporär nicht verfügbar',
  [EquipmentStatusEnum.UNKNOWN]: 'unbekannt',
}

const EquipmentStatusHint: { [key in EquipmentStatusEnum]: string } = {
  [EquipmentStatusEnum.EXISTING]: 'Keine IBN/ABN',
  [EquipmentStatusEnum.ADDITION]: 'Nur IBN: nicht verfügbar am Start, verfügbar am Ende',
  [EquipmentStatusEnum.DISMANTLING]: 'Nur ABN: verfügbar am Start, nicht verfügbar am Ende',
  [EquipmentStatusEnum.TEMP_AVAILABLE]:
    'IBN -> ABN: nicht verfügbar am Start, nicht verfügbar am Ende, aber zwischendurch kurzzeitig verfügbar',
  // [EquipmentStatusEnum.TEMP_NOT_AVAILABLE]:
  //   'ABN -> IBN: verfügbar am Start und Ende, aber nicht kurzzeitig zwischendurch',
  [EquipmentStatusEnum.UNKNOWN]: 'z.B. keine Schaltungen',
}

const Auth: { [key in keyof UserRead]: string } = {
  name: 'Benutzername',
  email: 'Email',
  admin: 'Rolle',
  settings: 'Benutzer Einstellungen',
  design: 'Design Einstellungen',
  id: 'Benutzer-ID',
  theme: 'Theme',
  last_login: 'letzter Login',
}

const BarTypes: { [key in BarTypesEnum]: string } = {
  [BarTypesEnum.VIOLATION_PARALLEL]: 'parallele Schaltungsblockprämisse',
  [BarTypesEnum.VIOLATION_PREVIOUS]: 'serielle Schaltungsblockprämisse',
  [BarTypesEnum.BACKGROUND]: 'möglicher Zeitraum',
  [BarTypesEnum.OVERLAP]: 'Überlappung',
  [BarTypesEnum.MAIN]: 'Schaltereignis',
  [BarTypesEnum.INITIAL_PERIOD]: 'Initialer Zeitraum',
  [BarTypesEnum.OFFLINE_RANGE]: 'Offline-Bereich durch IBN/ABN Schaltungen',
  [BarTypesEnum.LIMIT_VIOLATION]: 'Limit-Verletzung',
}

const UnlockBarsScope: { [key in UnlockBarsScopeEnum]: string } = {
  [UnlockBarsScopeEnum.BAR]: 'Schaltung isoliert von Schaltungsblock frei verschieben',
  [UnlockBarsScopeEnum.SWITCHING_BLOCK]: 'Schaltung und Schaltungsblock frei verschieben',
  [UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED]:
    'Schaltung, Schaltungsblock und parallele Schaltungsblöcke frei verschieben',
}
const UnlockBarsScopeGlobal: { [key in UnlockBarsScopeEnum]: string } = {
  [UnlockBarsScopeEnum.BAR]: 'Alle Schaltungen global freigeben',
  [UnlockBarsScopeEnum.SWITCHING_BLOCK]: 'Alle Schaltungsblöcke global freigeben',
  [UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED]: 'Alle verwandten Schaltungsblöcke global freigeben',
}

const ResolutionMode: { [key in ResolutionModeEnum]: string } = {
  auto: 'Automatisch',
  manual: 'Benutzerdefinierte Auflösung',
}

const DataTypes: { [key in DataTypesEnum]: string } = {
  string: 'Wort/Zeichenkette',
  number: 'Zahl',
  bigint: 'Große Zahl',
  boolean: 'boolesch',
  undefined: 'undefiniert',
  symbol: 'Symbol',
  null: 'Null',
  object: 'Schlüssel-Wert-Paar',
  function: 'Funktion',
  array: 'Liste',
}

const FilterOperator: { [key: string]: string } = {
  contains: '⊃ | enthält',
  eq: '= | gleich (=)',
  lt: '< | kleiner (<)',
  lte: '≤ | kleiner gleich (≤)',
  gt: '> | größer (>)',
  gte: '≥ | größer gleich (≥)',
}

const StartEndPreset: { [key in StartEndPresetEnum]: string } = {
  earliest_latest_switching_dates: 'Gesamten Kalender anzeigen',
  activity_data_filter_start_end: 'Gefilterten Zeitraum anwenden',
  start_and_intervals: 'Start und Intervalle',
}

const ColumnComputed: { [key in ColumnComputedEnum]: string } = {
  [ColumnComputedEnum.equipmentInSVG]: 'Betriebsmittel in SVG | in SVG',
  [ColumnComputedEnum.weekPattern]: 'Wochenprofil',
  [ColumnComputedEnum.continuousDaily]: 'Durchgehend/Täglich | Dgh./Tgl.',
}

const GanttChartResolution: { [key in ResolutionEnum]: string } = {
  twoYears: '2-Jährlich',
  year: 'Jährlich',
  quarter: 'Quartalsweise',
  month: 'Monatlich',
  week: 'Wöchentlich',
  day: 'Täglich',
}

const GanttChartResolutionPlurals: { [key in ResolutionEnum]: string } = {
  twoYears: '{count} mal 2-Jahre',
  year: '1 Jahr | {count} Jahre',
  quarter: '1 Quartal | {count} Quartale',
  month: '1 Monat | {count} Monate',
  week: '1 Woche | {count} Wochen',
  day: '1 Tag | {count} Tage',
}

const SwitchingPremiseViolationsDynamic: {
  [key in PremiseViolationDynamicEnum]: string
} = {
  offline: 'Offline-Prämisse verletzt',
  online: 'Online-Prämisse verletzt',
  protective: 'Schutzschaltungs-Prämisse verletzt',
}
const SwitchingPremiseViolationsStatic: {
  [key in SwitchingPremiseViolationStaticEnum]: string
} = {
  consecutive_activated: 'Schaltung vom Typ Inbetriebnahme (IBN) folgt ohne ABN auf eine weitere IBN.',
  consecutive_deactivated: 'Schaltung vom Typ Außerbetriebnahme (ABN) folgt ohne IBN auf eine weitere ABN.',
}

const ImportFormFormat: { [key in FileDataFormatEnum]: string } = {
  [FileDataFormatEnum.NEPTUN]: 'Neptun (standard)',
  [FileDataFormatEnum.NEPTUN_PLUS]: 'NeptunPlus',
  [FileDataFormatEnum.TBT]: 'TBT',
}

const SwitchingEvents: { [key in SwitchingEventsEnum]: string } = {
  [SwitchingEventsEnum.IN_RANGE_SWITCHING]: 'Ereignisse vorhanden', //Schaltereignisse vorhanden,
  [SwitchingEventsEnum.IN_RANGE_OFFLINE]: 'nicht Verfügbarkeit', // Schaltereignisse vorhanden + Betriebsmittel nicht Verfügbar,
}

const ImportFormFormatInfo: { [key in FileDataFormatEnum]: string } = {
  [FileDataFormatEnum.NEPTUN]: '',
  [FileDataFormatEnum.NEPTUN_PLUS]:
    'NeptunPlus definiert zusätzlich die Spalten "Start optimiert" und "Ende optimiert"',
  [FileDataFormatEnum.TBT]: '',
}

const TopologyChangesGroupingMode: {
  [key in TopologyChangesGroupingEnum]: string
} = {
  none: 'Keine Gruppierung',
  project: 'Nach Projekt',
  subproject: 'Nach Teilprojekt',
  project_and_subproject: 'Nach Projekt und Teilprojekt (hierarchisch)',
}

const SwitchingsEqualPaths: {
  [key in (typeof SwitchingEqualCheckPaths)[number]]: string
} = {
  start: TableCols.switching.start,
  end: TableCols.switching.end,
  switching_category_type: TableCols.switching.switching_category_type,
  duration_release_hours: TableCols.switching.duration_release_hours,
  'sub_project.name': TableCols.sub_project.name,
  'sub_project.project.name': TableCols.project.name,
  'sub_project.project.grid_operator': TableCols.project.grid_operator,
  'info_tbt.alternating': TableCols.switching_info_tbt.alternating,
  'info_tbt.continuous': TableCols.switching_info_tbt.continuous,
  'info_tbt.daily': TableCols.switching_info_tbt.daily,
  'info_tbt.saturday': TableCols.switching_info_tbt.saturday,
  'info_tbt.sunday': TableCols.switching_info_tbt.sunday,
  'info_tbt.workday': TableCols.switching_info_tbt.workday,
}

// noinspection HtmlUnknownTarget
export default {
  FilterOperator,
  sina: sina,
  $vuetify: de,
  _empty: '',
  empty: 'leer',
  // Dev
  DevLoginAutologinTriggered: 'Dev: Automatischer Login ausgelöst...',
  DevLoginAutologinAborted: 'Dev: Automatischer Login abgebrochen.',

  // Errors
  Error404: 'Nicht gefunden',
  Error401: 'Unbefugter Zugriff',
  Error500: 'Server Fehler',
  Error504: 'Gateway Zeitüberschreitung',
  Error: 'Fehler',
  ErrorServer: '<b>Der Server meldet einen Fehler:</b><br><br>{error}',
  ErrorUnknown: 'Unbekannter Fehler',
  Unknown: 'unbekannt',
  Undefined: 'nicht definiert',
  ChangesLostHint: 'Änderungen gehen verloren',

  // Buttons common
  ButtonYes: 'Ja',
  ButtonNo: 'Nein',
  ButtonBack: 'Zurück',
  ButtonContinue: 'Weiter',
  ButtonCancel: 'Abbrechen',
  ButtonAbort: 'Abbrechen',
  ButtonFinish: 'Abschließen',
  ButtonClose: 'Schließen',
  ButtonCopy: 'Kopieren',
  ButtonDuplicate: 'Duplizieren',
  ButtonApply: 'Anwenden',
  ButtonCompare: 'Vergleichen',
  ButtonReset: 'Zurücksetzen',
  ButtonEdit: 'Bearbeiten',
  ButtonDone: 'Fertig',
  ButtonCreate: 'Erstellen',
  ButtonAdd: 'Hinzufügen',
  ButtonSave: 'Speichern',
  ButtonSaveChanges: 'Änderungen speichern',
  ButtonRefresh: 'Aktualisieren',
  ButtonDelete: 'Löschen',
  ButtonDeleteMultiple: 'Löschen ({count})',
  ButtonRemove: 'Entfernen',
  ButtonExport: 'Exportieren',
  ButtonUpload: 'Hochladen',
  ButtonDownload: 'Herunterladen',
  ButtonDownloadZip: 'Zip Herunterladen',
  ButtonStart: 'Starten',
  ButtonCSV: 'Export CSV',

  // Common
  AlertLoosingChanges: 'Sicher? Alle nicht gespeicherten Änderungen gehen verloren.',
  CopiedToClipboard: 'In Zwischenablage kopiert',
  CopiedToClipboardFailed: 'In die Zwischenablage kopieren fehlgeschlagen. Siehe Logs in der Konsole (F12)',
  DateFrom: 'Ab',
  Yes: 'Ja',
  No: 'Nein',
  DateTo: 'bis',
  DateStart: 'Beginn',
  DateEnd: 'Ende',
  Show: 'anzeigen',
  ShowNot: 'nicht anzeigen',
  Sure: 'Sicher?',
  CommonOpen: 'Öffnen',
  CommonCopy: 'Kopie',
  CommonStep: 'Schritt {count}',
  CommonRows: 'Zeile | Zeilen',
  CommonIssues: 'Problem | Probleme',
  CommonPrint: 'Drucken',
  SureRemove: 'Wirklich entfernen?',
  CommonExportXLSX: 'Als Excel-Datei exportieren',
  CommonExportCSV: 'Als CSV exportieren',
  CommonExportElementResolveError: 'Konnte Tabelle zum exportieren nicht bestimmen. {additional}',
  CommonSVGElements: '{count} SVG-Element | {count} SVG-Elemente',
  NetworkSVGEquipmentNotInDiagram: 'Betriebsmittel wurde im Netzdiagramm nicht gefunden',
  CommonId: 'Id',
  CommonName: 'Name',
  CommonTimeframe: 'Zeitraum | Zeiträume',
  CommonTopologyPeriod: 'Topologie-Schritt | Topologie-Schritte',
  CommonTopology: 'Topologie | Topologien',
  CommonDays: '0 Tage | {count} Tag | {count} Tage',
  CommonHours: '0 Stunden | {count} Stunde | {count} Stunden',
  CommonPage: 'Seite',
  CommonPages: '0 Seiten | {count} Seite | {count} Seiten',
  CommonPagesSingle: 'Seite {count}',
  CommonPagesOf: 'Seite {current} / {total}',
  CommonPagesRange: 'Seiten {start} - {end}',
  CommonSelected: 'keine ausgewählt | {count} ausgewählt | {count} ausgewählt',
  CommonSelectedOf: '{count} von {all} ausgewählt',
  CommonDate: 'Datum',
  CommonEquipment: 'Betriebsmittel',
  CommonProject: 'Projekt | Projekte',
  CommonSubProject: 'Teilprojekt | Teilprojekte',
  CommonSwitchingCategory: 'Schaltungskategorie | Schaltungskategorien',
  CommonVoltageLevel: 'Spannungsebene',
  CommonColor: 'Farbe',
  CommonItems: 'ein Element | {count} Elemente',
  CommonMissing: 'fehlt',
  CommonOverwriteHint: 'Wird überschrieben',
  ShowDetails: 'zeige Details',
  Details: 'Details',
  // NetworkStateOnline: 'Verfügbare Betriebsmittel',
  // NetworkStateOffline: 'Inaktive Betriebsmittel',

  // Other
  EntityTableResizeHint: 'Feste Breite mit Rechtsklick zurücksetzen.',

  // Fetch Error
  FetchErrorTitle: 'Es ist ein Fehler beim Laden der Daten aufgetreten.',
  FetchErrorRetry: 'Erneut versuchen',

  // Data
  CommonEmpty: 'Keine Daten',
  NoData: 'Keine Daten',
  CreateSuccessful: 'Erstellen erfolgreich',
  SaveSuccessful: 'Speichern erfolgreich',
  UploadSuccessful: 'Upload erfolgreich',
  UpdateSuccessful: 'Aktualisierung erfolgreich',
  DeleteSuccessful: 'Löschen erfolgreich',
  Loading: 'Elemente werden geladen...',

  // Form rules
  FormValidationRequired: 'Pflichtfeld',
  FormValidationFilename: 'Ungültige Zeichen im Dateinamen',
  FormValidationFilenameMinMax: 'Wert muss zwischen {min} und {max} Zeichen lang sein.',
  FormValidationPassword: 'Passwort stimmt nicht überein.',
  FormValidationNumberRequired: 'Tragen Sie eine Zahl ein.',
  FormValidationNumberMin: 'Die Zahl muss größer gleich {number} sein.',
  FormValidationLengthMin: 'Muss mindestens {min} Zeichen lang sein.',
  FormValidationLengthMax: 'Darf maximal {max} Zeichen lang sein.',
  FormSave: 'Abspeichern',
  FormUpdate: 'Aktualisieren',
  FormSearch: 'Suchen',

  // Service Status
  ServiceHealthChecks,
  ServiceStatusTitle: 'Status der Backend-Dienste',
  ServiceStatusHealthy: 'Verfügbar',
  ServiceStatusUnhealthy: 'Nicht verfügbar',
  ServiceStatusAllHealthy: 'Alle Dienste verfügbar',
  ServiceStatusAllHealthyRestored: 'Alle Dienste wieder verfügbar',
  ServiceStatusSomeUnhealthy: 'Einige Dienste nicht verfügbar',
  ServiceStatusUndefined: 'kein Status',
  ServiceStatusFatal: 'Backend kann nicht erreicht werden',
  ServiceStatusLastChecked: 'Zuletzt geprüft',
  ServiceStatusWebsocketConnection: 'Live-Verbindung',
  ServiceStatusWebsocketHint:
    'Wir nutzen Websockets für Live-Updates. Wenn die Verbindung aktiv ist, kann die Anwendung Benachrichtigungen vom Server empfangen.',
  WebsocketConnectFailed: 'Live-Verbindung zum Websocket-Server konnte nicht hergestellt werden: {reason}',
  WebsocketReconnected: 'Live-Verbindung wiederhergestellt',

  // User
  User: 'User',
  Admin: 'Admin',
  CurrentUser: 'Aktueller Benutzer',
  UserProfileTitle: 'Benutzerprofil',

  // User Management Table
  UserManagementTimeOut:
    'Es hat sich <b>ein Nutzer</b> seit über einem Jahr nicht eingeloggt. | Es haben sich <b>{count} Nutzer</b> seit über einem Jahr nicht eingeloggt.',
  UserManagementTimeOutActionGoto: 'Zur Benutzerverwaltung',
  UserManagementTableId: 'Id',
  UserManagementTableName: 'Name',
  UserManagementTableEmail: 'Email',
  UserManagementTableAdmin: 'Admin',
  UserManagementTableTheme: 'Theme',
  UserManagementTableLast_login: 'Letzter Login',
  UserManagementTableAction: 'Aktion',
  UserManagementTableLastLoginNever: 'nie',
  UserManagementTableDeleteEnabled: 'Löschen aktiviert, letzter Login >= 1 Jahr',
  UserManagementTableDeleteDisabled: 'Login ≤ 1 Jahr, Löschen deaktiviert',

  // User Manuals Table
  UserManualFileTableId: 'Id',
  UserManualFileTableFile_name: 'Dateiname',
  UserManualFileTableSize: 'Größe',
  UserManualFileTableCreated_at: 'Erstellt',
  UserManualFileTableCreated_by: 'Erstellt durch',
  UserManualFileTableUpdated_at: 'Verändert',
  UserManualFileTableUpdated_by: 'Verändert durch',
  UserManualFileTableAction: 'Aktion',

  UserManualFileUploadTitle: 'Benutzerhandbuch hochladen',
  UserManualFileUploadFormFileLabel: '.pdf Datei',

  // App
  AppName: 'HAnS',
  AppBrand: 'by GRIDSIDE',
  AppOwner: 'TransnetBW | TNBW',
  AppDevelopment: 'Entwicklung',
  AppEnvironment: 'Umgebung',
  AppProgressNotificationsTitle: 'Hintergrundprozesse',
  AppProgressNotificationsActive: 'keine aktiven Vorgänge | {count} aktiver Vorgang | {count} aktive Vorgänge',
  AppProgressNotificationsClear: 'Leeren',
  AppProgressNotificationsEmpty: 'Noch keine Fortschrittsmeldungen vorhanden.',
  AppProgressNotificationsCreatedAt: 'Erstellt',
  AppProgressNotificationsCompletedAt: 'Abgeschlossen',
  AppProgressNotificationsUpdatedAt: 'Zuletzt aktualisiert',
  AppProgressNotificationsRuntime: 'Laufzeit',
  AppVersion: 'Version',
  AppFrontend: 'Frontend',
  AppBackend: 'Backend',

  AppActionAbout: 'Über HAnS',
  AppActionExportView: 'Ansichten und Favoriten exportieren',
  AppActionImportView: 'Ansichten und Favoriten importieren',
  AppActionSettings: 'Einstellungen und Personalisierung für diesen Schaltungskalender',
  AppActionReset: 'Alle Daten zurücksetzen',
  AppActionSwitchingPremises: 'Schaltungsprämissen',
  AppActionLogout: 'Abmelden',
  AppWhoops: 'Whoops',
  AppHome: 'Zum Hauptseite zurück',
  AppPageError404: '404 - Die gesuchte Seite existiert nicht.',
  AppPageError: 'Es ist ein Fehler aufgetreten.',
  AppActionDashboard: 'Zum Modul-Dashboard',
  AppActionActivities: 'Schaltungskalender',
  AppActionUserManagement: 'Benutzerverwaltung',
  AppActionUserManualFiles: 'Benutzerhandbuch-Dateien',
  AppActionLogoutSuccess: 'Abmelden erfolgreich!',
  AppActionSwapAction: 'Schaltungskalender wechseln',
  AppActionNavigationDrawer: 'Als Navigationsleiste',
  AppActionSyncDates: 'Datum über Fenster hinweg synchronisieren',
  AppActionTimeFinalize: 'Alle Schaltungszeiten finalisieren',
  AppActionTimeReset: 'Alle Schaltungszeiten zurücksetzen',

  // Activity Data Toolbar
  ActivityDataToolbarExportCSV: 'Daten als CSV exportieren.',
  ActivityDataToolbarExport: 'Daten werden exportiert.',
  ActivityDataToolbarExportPDFTooltip: 'Daten als PDF Exportieren',
  ActivityDataToolbarPrintPreviewError: 'Fehler beim erstellen der Preview. Fallback auf download...',

  // Activity Data Highlight
  ActivityDataHighlightActivator: 'Datum oder Topologie hervorheben',
  ActivityDataHighlightReset: 'Zurücksetzen auf Gesamttopologie',
  ActivityDataHighlightAll: 'Alle Topologien',
  ActivityDataHighlightTabCalendar: 'Kalender',
  ActivityDataHighlightTabTopologies: 'Topologien',
  ActivityDataHighlightTabTopologiesRefreshNeeded: 'Topologien sind veraltet und müssen neu berechnet werden',
  ActivityDataHighlightTabTopologiesRefresh: 'Topologien neu berechnen',
  ActivityDataHighlightTabTopologyPreview: 'Topologie-Vorschau',
  ActivityDataHighlightTabTopologyCount: 'Topologie kommt {count} mal vor',
  ActivityDataHighlightTabTopologyOnline: 'online',

  // Activity Data Filter
  DataFilterAllSelect: 'alle auswählen',
  DataFilterAllSelectResult: '{count} hinzufügen',
  DataFilterAllDeselect: 'alle abwählen',
  DataFilterSelected: "{base}{'|'}{selected} ausgewählt",
  DataFilterContain: 'welche {search} enthalten |welches {search} enthält |welche {search} enthalten',
  DataFilterContainLabel: 'Name enthält',
  DataFilterAll: 'Alle',
  DataFilterShowing: 'Angezeigt',
  DataFilterOf: 'von',
  DataFilterMore: '+ {count} weitere',
  CommonMore: '... und {count} weitere',

  DataFilterProject: 'keine Projekte | Projekt | {count} Projekte',
  DataFilterSubProject: 'keine Teilprojekte | Teilprojekt | {count} Teilprojekte',
  DataFilterType: 'keine Schaltungskategorien|Schaltungskategorie|{count} Schaltungskategorien',
  DataFilterEquipment: 'keine Betriebsmittel|Betriebsmittel|{count} Betriebsmittel',
  DataFilterEquipmentType: 'keine Betriebsmitteltypen|Betriebsmitteltyp|{count} Betriebsmitteltypen',
  DataFilterVoltageLevel: 'keine Spannungsebenen|Spannungsebene|{count} Spannungsebenen',
  DataFilterVoltageLevelDivider: 'weitere Spannungsebenen',

  DataFilterEquipmentConflicts: 'nur Betriebsmittel mit Konflikten anzeigen',
  DataFilterDate: 'Datum',
  DataFilterSwitchingEvent: 'Schaltungen',
  DataFilterFrom: 'von',
  DataFilterTo: 'bis',
  DataFilterGroupProject: 'Projektfilter',
  DataFilterGroupEquipment: 'Betriebsmittelfilter',
  DataFilterGroupSwitching: 'Schaltungsfilter',

  SwitchingEvents,
  SwitchingEventsAll: 'keine Filterung', // alle Ereignisse
  SwitchingEventsOfflineRangeHint: 'kein Effekt wenn in Ansicht "Schaltungsmatrix"',

  DataFilterApplyRange: 'Datum anwenden',
  DataFilterReset: 'Zurücksetzen',
  DataFilterImport: 'Import',
  DataFilterExport: 'Export',
  DataFilterSaveSettings: 'Speichern',
  DataFilterOpenSettings: 'Laden...',
  DataFilterSaveAndNameSettings: 'Einstellungen benennen und speichern',
  DataFilterNameExportFile: 'Exportierte Datei benennen',
  DataFilterNameDbEntry: 'Datenbankeintrag benennen',
  DataFilterFileImportFileSuccessful: 'Datei erfolgreich importiert',
  DataFilterFileImportErrorJson: '<b>Fehler beim Einlesen der JSON-Datei:</b><br/> {error}',
  DataFilterFileImportErrorZod: 'Fehler in der JSON-Schema Validierung',
  DataFilterFileImportErrorZodPath: 'Attribut',
  DataFilterFileImportErrorZodSchema: 'JSON-Schema',
  DataFilterFileImportErrorZodSchemaNoob: 'Schema',
  DataFilterFileImportErrorZodMessage: 'Fehler',
  DataFilterSaveSettingsSuccessful: 'Einstellungen erfolgreich gespeichert',
  DataFilterSettingsSuccessfullyApplied: 'Einstellungen erfolgreich angewandt',
  DataFilterSettingsDeleted: 'Einstellungen gelöscht',

  // Confirm dialog
  ConfirmReset: 'Auf Standardwerte setzen?',
  ConfirmDialogButtonYes: 'Ja',
  ConfirmDialogButtonNo: 'Nein',
  ConfirmDialogTitleDefault: 'Bestätigung notwendig.',
  ConfirmDialogTitleDelete: 'Wirklich löschen?',
  ConfirmDialogTitleDeleteMultiple: 'Wirklich {count} Elemente löschen?',
  ConfirmDialogCannotBeUndone: 'Sicher? Aktion kann nicht rückgängig gemacht werden.',

  // SVG Upload
  SvgUploadTitle: 'Netzdiagramm-SVG Datei laden',
  SvgUploadFormFileLabel: '.svg Datei',
  SvgUploadDownloadCurrent: 'Bisheriges SVG herunterladen',
  SVGUploadDownloadMissing: 'Schaltungskalender hat kein SVG.',
  SVGUploadSuccess: 'Datei erfolgreich importiert',
  SVGUploadMissing: 'Bitte wählen Sie eine Datei.',
  SVGUploadWarnings: 'Es sind Fehler beim Upload aufgetreten',

  // Import
  ImportTitle: 'Daten aus CSV-Datei laden',
  ImportTitleValidation: 'Daten Validierung',
  ImportTitleResult: 'Neu erstellte Datensätze',
  ImportFormFileLabel: '{extension} Datei',
  ImportFormFileEncoding: 'Zeichensatz (encoding)',
  ImportFormFileDescriptionLabel: 'Beschreibung',
  ImportEntities,
  ImportNoNewEntities: 'Keine neuen Datensätz angelegt.',

  ImportFormFormatSelect: 'Datenformat:',
  ImportFormFormat,
  ImportFormFormatInfo,

  ImportDownloads: 'Downloads',
  ImportDownloadsFormat: 'Format',
  ImportDownloadsTemplate: 'Template CSV',
  ImportDownloadsExampleCSV: 'Beispiel CSV',
  ImportDownloadsNeptun: 'Neptun-Format',
  ImportDownloadsNeptunPlus: 'NeptunPlus-Format',
  ImportExportCurrentData: 'Bisherige Daten herunterladen',
  ImportDataNeptun: 'Neptun',
  ImportDataTBT: 'TBT',
  ImportButtonValidate: 'Daten validieren',
  ImportButtonValidationErrors: 'Es sind Fehler/Warnungen in der Validierung. Trotzdem fortfahren?',
  ImportButtonCTA: 'Daten Laden',
  ImportFileSuccess: 'Datei erfolgreich importiert',
  ImportFileMissing: 'Bitte wählen Sie eine Datei.',
  ImportFileSelect: 'Datei auswählen.',
  ImportFileWrongFormat: 'Datei hat nicht das richtige Format.',
  ImportFormFileDropZoneInfo: 'Klicken oder Datei via Drag & Drop hochladen.',
  ImportWarnings: 'Es sind Warnungen beim Import aufgetreten',
  ImportServerErrorInfo: '',
  ImportServerErrorDetails: 'Zeige details',
  // ImportServerErrorInfo: 'Es können keine Import-Fehler angezeigt werden.',
  ImportServerError: 'Import ist mit einem Server Error fehlgeschlagen',

  // Export
  ExportTitle: 'Daten als CSV-Datei exportieren',
  ExportFormFileDescriptionLabel: 'Bezeichnung',
  ExportFormFileNameLabel: 'Exportierter Dateiname',
  ExportFormNeptunPlusLabel: 'NeptunPlus-Format',
  ExportButtonCTA: 'Daten exportieren',

  /* --- Pages --- */
  // Pages Names
  PageSwitchingDiagram: 'Schaltungsdiagramm',
  PageProjects: 'Projekte',
  PageSubProjects: 'Teilprojekte',
  PageEquipments: 'Betriebsmittel',
  PageSwitchings: 'Schaltvorgänge',
  PageNetworkMap: 'Netzdiagramm',
  PageTopologies: 'Schaltungsmatrix',
  PageReport: 'Bericht erstellen',
  PageSwitchingPremises: 'Schaltungsprämissen',
  PageLogin: 'Login',
  PageIndex: 'Schaltungskalender',
  PageDashboard: 'Dashboard',
  PageActivities: 'Schaltungskalender',
  PageActivityCompare: 'Schaltungskalender vergleichen',
  PageRegister: 'Registrieren',
  PageAdmin: 'Admin-Panel',
  PageAdminUserManagement: 'Admin - Benutzerverwaltung',
  PageUserManagement: 'Benutzerverwaltung',
  PageUserManualFiles: 'Benutzerhandbuch-Dateien',
  PageStorageDebug: 'Storage-Debug',

  // Dashboard
  DashboardModulesTitle: 'Module',
  DashboardModulesSubtitle: 'Wählen Sie ein Modul aus, um fortzufahren',
  DashboardModuleHAnS: 'HAnS Schaltungskalender',
  DashboardModuleHAnSDescription:
    '<strong>H</strong>ilfsprogramm zur <strong>An</strong>alyse von <strong>S</strong>chaltungen',
  DashboardModuleSINA: 'SINA',
  DashboardModuleSINADescription: '<strong>Si</strong>mulation und <strong>N</strong>etz<strong>a</strong>uswertung',
  DashboardModuleAdmin: 'Admin',
  DashboardModuleUserManagement: 'Benutzerverwaltung',
  DashboardModuleUserManagementDescription: 'Verwaltung von Benutzern und Berechtigungen',
  DashboardModuleManuals: 'Handbücher',
  DashboardModuleManualsDescription: 'Benutzerhandbücher und Dokumentation',
  DashboardModuleStorageDebug: 'Remote-Filesystem',
  AdminDatabaseSizesTitle: 'Datenbankgrößen',
  AdminDatabaseSizesEmpty: 'Keine Informationen zu den Datenbankgrößen vorhanden.',
  AdminDatabaseTablesTotalSize: 'Daten in Tabellen',
  AdminDatabaseUserManualFilesTotalSize: 'Benutzerhandbuch-Dateien',
  AdminDatabaseLargeObjectTotalSize: 'Gespeicherte Ergebnisdateien',
  AdminDatabaseLargeObjectTotalSizeHint:
    'Das ist der Speicherplatz aller SINA-Ergebnisdateien, die direkt in der Datenbank abgelegt sind. Falls dieser Wert stark von der Datenbankgröße abweicht, sollte die DB-Administration ein Postgres `VACUUM FULL pg_largeobject` prüfen.',
  AdminDatabaseOrphanLargeObjectCount: 'Nicht zugeordnete Elemente',
  AdminDatabaseOrphanLargeObjectCountHint:
    'Diese Ergebnisdateien belegen noch Speicherplatz, sind aber keinem aktuellen Eintrag mehr zugeordnet.',
  AdminDeleteSinaOrphansButton: 'bereinigen',
  AdminDeleteSinaOrphansTitle: 'Nicht zugeordnete Ergebnisdateien löschen?',
  AdminDeleteSinaOrphansConfirm: 'Sollen die {count} nicht zugeordneten Ergebnisdateien endgültig gelöscht werden?',
  AdminDeleteSinaOrphansSuccess: '{count} nicht zugeordnete Ergebnisdateien wurden gelöscht.',
  AdminUserManagementLinkDescription: 'Benutzer prüfen, Berechtigungen verwalten und inaktive Accounts erkennen',
  AdminUserManagementTotalUsers: 'Benutzer gesamt',
  AdminUserManagementInactiveUsers: 'Inaktiv seit > 1 Jahr',
  AdminServiceStatusConnected: 'Verbunden',
  AdminServiceStatusDisconnected: 'Getrennt',
  AdminServiceStatusConnecting: 'Verbinde',
  AdminStorageSummaryFiles: 'Dateien',
  AdminStorageSummaryDirectories: 'Verzeichnisse',
  AdminStorageSummarySize: 'Gesamtgröße',
  AdminStorageSummaryRootChip: '{root}: {files} Dateien, {directories} Verzeichnisse, {size}',
  StorageDebugTitle: 'Remote-Filesystem',
  StorageDebugNoEntries: 'Keine Verzeichniseinträge gefunden.',
  StorageDebugDirectoryItemCount: '{count} Objekte | {count} Objekt | {count} Objekte',
  StorageDebugPreviewUnsupported: 'Dieser Dateityp kann nicht direkt in der Vorschau angezeigt werden.',
  StorageDebugPreviewLoadingFailed: 'Vorschau konnte nicht geladen werden.',
  StorageDebugPreviewPreparingDownload: 'Download wird vorbereitet...',
  StorageDebugPreviewDownloading: 'Vorschau wird heruntergeladen...',
  StorageDebugPreviewRendering: 'Vorschau wird gerendert...',
  StorageDebugPathMissing: 'Verzeichnis nicht gefunden: {path}',
  StorageDebugDownloadPreparingDownload: 'Download wird vorbereitet...',
  StorageDebugDownloadDownloading: 'Datei wird heruntergeladen...',
  StorageDebugDownloadRendering: 'Download wird abgeschlossen...',

  // Activity Page
  ActivityIdUndefined: 'Schaltungskalender nicht definiert',
  ActivitySearch: 'Suche',
  ActivityTableCaption: 'verfügbare Schaltungskalender',
  ActivityTableColumnId: 'ID',
  ActivityTableColumnName: 'Schaltungskalender Name',
  ActivityTableColumnUserId: 'Benutzer ID',
  ActivityTableColumnUserName: 'Benutzername',
  ActivityTableColumnStats: 'Statistik',
  ActivityTableColumnCreatedAt: 'erstellt am',
  ActivityStatsProjects: 'Projekte',
  ActivityStatsSubProjects: 'Teilprojekte',
  ActivityStatsEquipments: 'Betriebsmittel',
  ActivityStatsSwitchings: 'Schaltungen',
  ActivityStatsSwitchingBlocks: 'Schaltungsblöcke',

  ActivityCTACreate: 'Schaltungskalender erstellen',
  ActivityCreateLabel: 'Name',
  ActivityCTASelect: 'Schaltungskalender auswählen',
  ActivityShowAllUsers: 'Schaltungskalender von anderen Benutzern anzeigen',

  // Activity Compare
  ActivityCompareTitle: 'Schaltungskalender',
  ActivityCompareReference: 'Referenz',
  ActivityCompareSelectedCount: 'Keine Schaltungskalender | {n} Schaltungskalender | {n} Schaltungskalender',
  ActivityCompareGroupRows: 'Nach Schaltungskalender gruppieren',
  ActivityCompareGroupRowsHint: 'Nur möglich, wenn Betriebsmittel nicht in Referenz-Schaltungskalender vorkommen',
  ActivityCompareHideReference: 'Referenz ausblenden',
  ActivityCompareResetReference: 'Referenz zurückgesetzt',
  ActivityCompareEquipmentCount: 'Keine Betriebsmittel | {n} Betriebsmittel | {n} Betriebsmittel',
  ActivityCompareMisc: 'Schaltungskalender #{0}',
  ActivityCompareSwitchingsNoData: 'Keine Schaltungen',
  SwitchingsEqualPaths,

  // Auth User Keys
  Auth,

  // Auth login
  LoginFormTitle: 'Login',
  LoginFormButtonSubmit: 'Anmelden',
  LoginFormLabelName: 'Benutzer',
  LoginFormLabelNamePlaceholder: 'Benutzer',
  LoginFormLabelPassword: 'Passwort',
  LoginSuccess: 'Anmelden erfolgreich! ',
  LoginFailed: 'Anmelden fehlgeschlagen! ',
  LoginSessionExpires: 'Sitzung läuft in {minutes} Minuten ab! ({date}) ',
  LoginJWTMissing: 'JWT nicht gefunden.',
  LoginSOO: 'Mit SSO einloggen',
  LoginSOODebugExpand: 'SSO Debuggen',
  LoginSOODebugRequest: 'Debug SSO Request',
  LoginSOODebugRequestMetadataIdp: 'Metadata des IDP',
  LoginSOODebugRequestMetadataIdpSubtitle: 'IDP von SP erreichbar',
  LoginSOODebugRequestMetadataSp: 'Metadata des SP (diese App) an',
  LoginSOODebugRequestMetadataSpSubtitle: 'SP Konfiguration',
  LoginSOODebugResponse: 'Debug SSO Response',
  LoginSOODebugRequestInfo: 'Zeige den Request an den IDP',
  LoginSOODebugRequestInfoSubtitle: 'Anfrage kann erfolgreich vom SP formuliert werden (SAML AuthNRequest)',
  LoginSOODebugResponseInfo: 'Zeige die Response vom IDP an den SP',
  LoginSOODebugResponseInfoSubtitle: 'Erfolgreiche Antwort vom IDP (SAMLResponse)',
  LoginSamlBack: 'Zurück zum Login.',
  LoginPending: 'Melde an...',
  LoginWelcome: 'Willkommen {user}!',
  LoginSuccessRedirect: 'Sie werden weitergeleitet...',
  LoginSuccessRedirectInCaseNot: 'Falls nicht, klicken hier',

  // Auth Register
  RegisterFormTitle: 'Registrieren',
  RegisterButton: 'Registrieren',
  RegisterUser: 'Benutzer registrieren',
  RegisterSuccess: 'Registrierung erfolgreich!',
  RegisterPasswordInfo: 'Das Passwort kann nicht wiederhergestellt werden, sind Sie sicher?',
  RegisterBack: 'zurück',
  RegisterFormLabelName: 'Benutzer',
  RegisterFormLabelPassword: 'Passwort',
  RegisterFormLabelPasswordRepeat: 'Passwort wiederholen',

  // Topologies
  TopologieTableSPVHeaderPremise: 'Prämisse',
  TopologieTableSPVHeaderEquipments: 'betroffene Betriebsmittel',
  TopologieTableSPVHeaderStatus: 'Status',
  TopologieTableSPVHeaderOnline: 'min. online / Normalbetrieb',
  TopologieTableSPVHeaderOffline: 'max. offline / Freischaltung',
  TopologieTableSPVHeaderProtected: 'max. Schutzschaltungen',

  TopologieCalcBroadcast: 'Berechne Topologien neu...',
  TopologieCalcError: 'Fehler beim berechnen der Topologien',
  TopologieTableHeaderTopologies: 'Topologien',
  TopologieTableHeaderTopology: 'Topologie #{count}',
  TopologieTableHeaderAmount: 'Anzahl Schritte',
  TopologieTableHeaderDescription: 'Beschreibung',
  TopologyOverlappingList: 'Überlagernde Schaltungen',
  TopologyOverlappingPriority: 'Vorrang',
  TopologyOverlappingListHint:
    'Bei der Topologieberechnung, in der mehrere gleichzeitig aktive Schaltungen einem Betriebsmittel zugeordnet sind, bestimmt eine Prioritätslogik die vorrangig zu berücksichtigende Schaltung.',
  TopologyNameEditTitle: 'Topologien bearbeiten',
  TopologyNameEditLabel: 'Name',
  TopologyNameEditRequired: 'Name ist erforderlich',
  TopologyNameEditMaxLength: 'Name darf maximal 100 Zeichen lang sein',
  TopologyNameEditTabTopologies: 'Topologien',
  TopologyNameEditTabPeriods: 'Topologie-Schritte',
  TopologyNameEditTabPeriodDefault: '{count}. Schritt',
  TopologyPeriodStepAndTopologyLabel: '{name} – {topology}',
  TopologyNameEditPeriodStep: 'Schritt',
  TopologyNameEditTabPeriodsHint:
    'Eine Topologie kann mehrfach vorkommen, dadurch besteht sie aus mehreren Schritten. Die Einstellungen werden nur im Browser gespeichert',
  TopologyPeriodTopologyTitle: 'Topologie',
  TopologyPeriodNameEditTitle: 'Topologie-Schritte bearbeiten',
  TopologyPeriodNameEditLabel: 'Schritt-Name',
  TopologyPeriodDescriptionLabel: 'Beschreibung',
  TopologyPeriodDescriptionPlaceholder: 'Optionale Beschreibung',
  TopologyPeriodDescriptionMaxLength: 'Beschreibung darf maximal 500 Zeichen lang sein',
  TopologieTableHeaderStart: 'Start',
  TopologieTableHeaderEnd: 'Ende',
  TopologieTableHeaderSwitchingPremise: 'Schaltungsprämisse verletzt',
  TopologieTableHeaderSwitchingPremiseViolated: 'Ja',
  TopologieTableHeaderEquipment: 'Betriebsmittel',

  // Switchings
  TableCols,
  TableColsArtificialHint:
    'Beschreibt, ob das Objekt künstlich erzeugt wurde und somit nicht beim Import eines Schaltungskalenders vorhanden war.',
  SwitchingsTableSearch: 'Schaltungen suchen...',
  SwitchingsTableFooterPaginator: 'Zeilen pro Seite',
  SwitchingsTableFooterPaginatorOf: 'von',
  SwitchingsTableFooterPaginatorAll: 'alle',
  SwitchingsTableColumnEquipment: 'Betriebsmittel',
  SwitchingsTableColumnStart: 'Start',
  SwitchingsTableColumnStartEarliest: 'Start min.',
  SwitchingsTableColumnEnd: 'Ende',
  SwitchingsTableColumnEndLatest: 'Ende max.',
  SwitchingsTableColumnDuration: 'Dauer (in Tagen)',
  SwitchingsTableColumnDurationReleaseHours: 'Rückschaltzeit',
  SwitchingsTableColumnSubProject: 'Teilprojekt',
  SwitchingsTableColumnSwitchingBlock: 'Schaltungsblock',
  SwitchingsTableColumnProject: 'Projekt',
  SwitchingsTableColumnSwitchingsCategory: 'Schaltungskategorie',
  SwitchingsTableColumnInfoTBT: 'Tage',
  SwitchingsTableInfoTBTWorkday: 'Werktags',
  SwitchingsTableInfoTBTSaturday: 'Samstags',
  SwitchingsTableInfoTBTSunday: 'Sonntags',
  SwitchingsTableColumnInfoTBTContinuous: 'Durchgängig',
  SwitchingsTableColumnInfoTBTAlternating: 'Wechselweise',
  SwitchingsTableColumnImportFile: 'Import Datei',
  SwitchingsTableShowInitialPeriods: 'Initiale Zeiträume anzeigen',
  SwitchingsTableAdditionalScaleMarks: 'Zusätzliche Achsenmarkierungen',
  SwitchingsTableAdditionalScaleMarksInfo: 'Zeigt basierend auf aktueller Auflösung zusätzliche Achsenmarkierungen',
  SwitchingsTableRowsMarkedSort: 'Markierte Zeilen nach oben sortieren',
  SwitchingsTableRowsTimeSwitchingSort: 'Schaltungen nach Gliederung und Schaltungsbeginn sortieren.',
  SwitchingsTableMarkerActivate: 'Timemarker aktivieren',
  SwitchingsTableMarkerDeactivate: 'Timemarker deaktivieren',
  SwitchingsTableSuccessfullyUpdated: 'Änderungen gespeichert',
  SwitchingsDurationReleaseHoursMissing: 'keine',
  SwitchingFormSectionOrganisation: 'Organisation',
  SwitchingFormSectionProperties: 'Eigenschaften',
  SwitchingFormSectionTBT: 'TBT',

  // Switching Blocks Table
  PageSwitchingBlocks: 'Schaltungsblöcke',
  SwitchingBlocksTableSearch: 'Schaltungsblöcke suchen...',
  SwitchingBlocksTableColumnSwitchingsCount: 'Anzahl Schaltungen',
  SwitchingBlocksTableColumnImportFile: 'Import Datei ID',
  SwitchingBlockDialogCreateTitle: 'Schaltungsblock erstellen',
  SwitchingBlockDialogEditTitle: 'Schaltungsblock bearbeiten',

  // Switching Dialog
  SwitchingDialogCreateTitle: 'Schaltung erstellen',
  SwitchingDialogEditTitle: 'Schaltung bearbeiten',

  // Switching Premise Dialog
  SwitchingPremiseDialogCreateTitle: 'Schaltungsprämisse erstellen',
  SwitchingPremiseDialogEditTitle: 'Schaltungsprämisse bearbeiten',

  // CRUD Messages

  SwitchingPremiseViolatedTooltipEquipments: 'Betroffene Betriebsmittel',
  SwitchingPremisesLiveCheckDeactivate: 'Live-Prüfung von Schaltungsprämissen deaktivieren',
  SwitchingPremisesLiveCheckActivate: 'Live-Prüfung von Schaltungsprämissen aktivieren',
  SwitchingPremisesManualCheck: 'Manuelle Prüfung von Schaltungsprämissen',

  SwitchingBlockRestrictionPrevious: 'Schaltungsblock-Prämisse "Serielle Abhängigkeit" verletzt',
  SwitchingBlockPremiseNonParallel: 'Nicht-parallele Schaltungsblock-Prämisse verletzt ',

  // Equipments
  EquipmentTypes,

  // Settings & User Settings
  SettingsAndPersonalization: 'Einstellungen und Personalisierung',
  SettingsThemeGlobal: 'Globales Theme',
  Theme,
  SVGLineStyle,

  // Settings bars coloring
  SwitchingsColoringMode: 'Schaltungen einfärben',
  ColoringMode,
  SettingsColorByProjectDefault: 'Standard',
  SettingsColorByVoltageDefault: 'Sonstige',
  SettingsColorContextGantt: 'Gantt-Diagramm',
  SettingsColorContextSVG: 'Netzgrafik',
  SettingsColorContextSVGLine: 'Netzgrafik-Stil',

  BarTypes,

  SettingsSwitchingPremiseRestrictionsViolation: 'Verletzung Schaltung- und Schaltungsblockprämissen',
  SwitchingPremiseViolationsDynamic,
  SwitchingPremiseViolationsStatic,
  SettingsPreviewGantt: 'Vorschau Gantt',
  SettingsSwitchingPremiseViolationDynamic: 'Dynamische Schaltungsprämissen',
  SettingsSwitchingPremiseViolationStatic: 'Statische Schaltungsprämissen',
  SettingsSwitchingPremiseStaticActivated: 'Aufeinanderfolgende IBN',
  SettingsSwitchingPremiseStaticDeactivated: 'Aufeinanderfolgende ABN',

  SettingsSwitchingBlockRestrictionViolation: 'Schaltungsblockprämissen',
  SettingsSwitchingBlockRestrictionPrevious: 'Seriell',
  SettingsSwitchingBlockRestrictionParallelForbidden: 'Nicht-parallel',

  SettingsSwitchingBars: 'Balken der Schaltungen',
  SettingsSwitchingBarPossible: 'Möglicher Zeitraum',
  SettingsSwitchingBarOptimized: 'Optimierter Zeitraum',
  SettingsSwitchingBarInitial: 'Initialer Zeitraum',
  SettingsSwitchingBarLimitViolation: 'Überschreitung des Limits des Schaltungsblocks',
  SettingsSwitchingOfflineRange: 'Offline-Bereich durch IBN/ABN Schaltungen',

  ActivitySwitchingInfoLimitViolationStart:
    'Schaltung startet {count} Tag vor dem frühesten erlaubten Datum ({date}) | Schaltung startet {count} Tage vor dem frühesten erlaubten Datum ({date})',
  ActivitySwitchingInfoLimitViolationEnd:
    'Schaltung endet {count} Tag nach dem spätesten erlaubten Datum ({date}) | Schaltung endet {count} Tage nach dem spätesten erlaubten Datum ({date})',
  SettingsSwitchingEquipmentState: 'Netzzustand',
  SettingsSwitchingEquipmentStateHint: '(Start u. Zielnetz)',
  SettingsSwitchingEquipmentStatus: 'Betriebsmittel-Status',
  SettingsOpacity: 'Deckkraft',
  SettingsHeight: 'Höhe',
  SettingsWidth: 'Breite',
  SettingsThickness: 'Dicke',
  SettingsGridLines: 'Gitternetzlinien Gantt',
  SettingsSecondaryBarsLabel: 'Sonstige Markierungen',
  SettingsRestoreDefaults: 'Standardwerte wiederherstellen',
  SettingsRestoreDefault: 'Standardwert wiederherstellen',
  SettingsSaveChanges: 'Änderungen speichern',
  SettingsSaveSuccessful: 'Änderungen gespeichert',

  SettingsApplyUserFavoritesHeader: 'Gewünschte Einstellung auswählen oder löschen',
  SettingsUserFavoritesLabel: 'Einstellungen',

  SettingsTbtDailyLabel: 'Schaltung Info TBT',
  SettingsTbtDailyTitle: 'Markierung täglich durchgeführter Schaltungen (TBT)',
  SettingsTbtDailyColor: 'Farbe',
  SettingsTbtDailySpacing: 'Abstand',
  SettingsTbtDailyAngle: 'Winkel',
  SettingsTbtDailyPreview: 'Vorschau',

  // Switchings Diagram - Ganttchart
  SwitchingDiagramLockDrag: 'Verschieben von Schaltungen sperren',
  SwitchingDiagramDragLimitHint: 'Schaltung kann nicht weiter verschoben werden. Grenze erreicht: {date}',
  SwitchingDiagramDragUnlockHint: 'Schaltung kann nicht verschoben werden. Entsperren notwendig.',
  SwitchingDiagramPlaceTimeMarker: 'Zeitmarkierung setzen',
  SwitchingDiagramLegend: 'Legende Anzeigen',
  SwitchingDiagramToggleExpand: 'Alle Zeilen ausklappen',
  SwitchingDiagramColSelect: 'Spalten auswählen',
  SwitchingDiagramRowHeight: 'Zeilenhöhe',
  SwitchingDiagramSeekHotkeyTooltipForward: 'Herunterscrollen innerhalb der Achsenbeschriftung',
  SwitchingDiagramSeekHotkeyTooltipBack: 'Hochscrollen innerhalb der Achsenbeschriftung',
  OfflineRangeType,

  // Network Map
  NetworkMapNoDate: 'ohne Datum',
  NetworkMapMissingSVG: 'Keine Netzdiagramm-SVG vorhanden',
  NetworkMapSVGCreationError: 'Konnte SVG nicht rendern',
  NetworkDisplayMode,
  NetworkDisplayModeStateHint,
  NetworkDisplayModeHeadline: 'Netzzustand',
  NetworkDisplayModeVoltageLevels: 'Spannungsebenen',
  NetworkDisplayModeEquipmentTypes: 'Betriebsmitteltypen',
  NetworkDisplayModeStates: 'Darstellung Start- und Zielnetz',
  NetworkStateSVGMissing: 'Nicht in Netzdiagramm eingebunden',
  NetworkShowSVGInfo: 'Zeige Detailinformationen',
  NetworkStateColoring,
  EquipmentNetworkState,

  // Activity Switching Info
  ActivitySwitchingInfoLabelStart: 'Start',
  ActivitySwitchingInfoLabelEnd: 'Ende',
  ActivitySwitchingInfoSwitchingCategory: 'Schaltungskategorie',
  ActivitySwitchingInfoStart: 'Start',
  ActivitySwitchingInfoStartMin: 'min.',
  ActivitySwitchingInfoEnd: 'Ende',
  ActivitySwitchingInfoEndMax: 'max.',
  ActivitySwitchingInfoNoDate: 'nicht gesetzt',
  ActivitySwitchingInfoFixedDate: 'unveränderbar',
  ActivitySwitchingInfoTBTContinuous: 'durchgängig',
  ActivitySwitchingInfoTBTDaily: 'täglich', // same as continuous = False
  ActivitySwitchingInfoTBTAlternating: 'wechselweise', // same as continuous = False
  ActivitySwitchingInfoSwitchingBlock: 'Schaltungsblock',
  UnlockBarsScope,
  UnlockBarsScopeGlobal,
  ActivitySwitchingInfoSwitchingBlockUnlockHint:
    'Ermöglicht das verschieben von Schaltungen außerhalb der min/max grenzen von Schaltungsblöcken',
  ActivitySwitchingInfoSwitchingBlockRelatedSwitchings: 'Verwandte Schaltungen',
  ActivitySwitchingInfoNoRelatedSwitchings: 'Keine verwandten Schaltungen',
  ActivitySwitchingInfoSwitchingBlockRestrictions: 'Schaltungsblockprämissen',
  ActivitySwitchingInfoSwitchingBlockRestrictionsThisHint: '(von dieser Schaltung)',
  ActivitySwitchingInfoswitch_block_previous: 'seriell',
  ActivitySwitchingInfoswitch_block_parallel_forbidden: 'nicht-parallel',
  ActivitySwitchingInfoswitch_block_parallel: 'parallel',
  ActivitySwitchingDateSelectIndividualSelection: 'Start der Schaltung',
  ActivitySwitchingDateSelectCustomTime: 'Eigene Uhrzeit',
  ActivitySwitchingDateSelectTime: 'Startzeit',
  ActivitySwitchingDateSelectHour: 'Uhr',
  ActivitySwitchingDateSelectEarliestStartDateViolated: 'Frühestmögliches Startdatum verletzt',
  ActivitySwitchingDateSelectLatestEndDateViolated: 'Spät möglichstes Enddatum verletzt',
  ActivitySwitchingDateSelectStartEndDateViolated: 'Frühestmögliches Startdatum und spät möglichstes Enddatum verletzt',

  SwitchingDiagrammDialogSwitchingPremiseTooltip: 'Schaltungsprämissen verwalten',
  SwitchingDiagrammDialogSwitchingPremiseCreateCTA: 'Schaltungsprämisse erstellen',
  SwitchingDiagrammDialogSwitchingPremiseFormEquipmentIds: 'Betriebsmittel',
  SwitchingDiagrammDialogSwitchingPremiseFormAmountMaxOffline: 'max. offline / Freischaltung',
  SwitchingDiagrammDialogSwitchingPremiseFormAmountMaxProtective: 'max. Schutzschaltung',
  SwitchingDiagrammDialogSwitchingPremiseFormAmountMinOnline: 'min. online / Normalbetrieb',
  SwitchingDiagrammDialogSwitchingPremiseFormStart: 'Start',
  SwitchingDiagrammDialogSwitchingPremiseFormEnd: 'Ende',
  SwitchingDiagrammDialogSwitchingPremiseFormNote: 'Notiz',

  SwitchingDiagramViolationEarliestStartLatestEnd:
    'Frühestmöglicher Schaltungsbeginn bzw. spätestes Schaltungsende verletzt',

  SwitchingDiagramDefaultStart: 'Zum ersten Startdatum springen',
  GroupingPresetOption,
  SwitchingsTimeAxisLabel,

  GanttChartResolution,
  GanttChartResolutionPlurals,
  SwitchingType,

  StartEndPreset,
  EarliestLatestSwitchingDatesDisabledHint: 'deaktiviert - notwendige Daten konnten nicht ermittelt werden.',
  ResolutionMode,
  ResolutionModeTitle: 'Auflösung',
  SwitchingTypeDisabled: 'Freischaltung',
  SwitchingTypeProtected: 'Schutzschaltung',

  SwitchingActionFinalize: 'finalisieren',
  SwitchingActionReset: 'zurücksetzen',

  // User Configuration
  UserConfigurationTableLabel: 'Bezeichnung',
  UserConfigurationTableKey: 'Schlüssel',
  UserConfigurationTableExample: 'Beispiel',
  UserConfigurationTableType: 'Typ',
  DataTypes,

  UserConfigurationExampleOneOf: 'Eins von: {oneOf}',

  UserConfigurationSwitchingColoring: 'Farbeinstellungen für Gantt und SVG',
  UserConfigurationSwitchingColoringColorMode: 'Färben nach ...',

  /// Activity Data Filter
  UserConfigurationFilter: 'Filter',
  UserConfigurationFilterProject: 'Projekt',
  UserConfigurationFilterEquipment: 'Betriebsmittel',
  UserConfigurationFilterSubProject: 'Teilprojekt',
  UserConfigurationFilterSwitchingCategory: 'Schaltungskategorie',
  UserConfigurationFilterDatesRange: 'Filter Datumsbereich',
  UserConfigurationFilterDatesRangeSwitchingEvents: 'Schaltereignisse',
  UserConfigurationFilterDatesRangeStart: 'Start (ISO 8601)',
  UserConfigurationFilterDatesRangeEnd: 'Ende (ISO 8601)',
  UserConfigurationFilterEquipmentConflicts: 'Betriebsmittel mit Konflikten',
  UserConfigurationFilterVoltageLevel: 'Spannungsebene in kV',

  /// Time Marker
  UserConfigurationTimeMarker: 'Zeitmarkierung (ISO 8601)',
  UserConfigurationTimeMarkerBroadcast: 'Zeitmarkierung (ISO 8601)',

  /// Gantt Time Axis
  UserConfigurationTimeAxis: 'Einstellung der Zeitachse',
  UserConfigurationDateStartUnix: 'Gantt Start (ISO 8601)',
  UserConfigurationDateEndUnix: 'Gantt Ende (ISO 8601)',
  UserConfigurationResolution: 'Gantt Auflösung',
  UserConfigurationResolutionMode: 'Auflösung Modus',
  UserConfigurationResolutionStep: 'Auflösung Modus Manuell Schritte',
  UserConfigurationResolutionResolution: 'Auflösung Modus Einheit',

  /// Gantt Settings
  UserConfigurationGanttSettings: 'Allgemeine Gantt-Einstellungen',

  /// Gantt Columns
  UserConfigurationColumns: 'Einstellungen der Spalten',
  UserConfigurationColumnsWidthTotal: 'Relative Breite der Gantt-Spalten in %',
  UserConfigurationColumnsGrouped: 'Spalten nach denen gruppiert wird',
  UserConfigurationColumnsVisual: 'Spalten die werte von Schaltungen anzeigen',
  UserConfigurationColumnsExample: 'Zu komplex zum anzeigen. Herunterladen oder localStorage des Browsers einsehen',
  UserConfigurationColumnsGroupedPreset: 'Gruppierung',
  ColumnComputed,

  // SVG Preview
  SVGPreviewTitle: 'Netzgrafik-Vorschau',
  SVGPreviewActions: 'Zoome und verschiebe mit Mausgesten.',

  // PDF Report (AI)
  GeneratePDFReport: 'PDF-Report erstellen',
  StateSelection: 'Zustandsauswahl',
  CurrentStateOnly: 'Nur aktueller Zustand',
  AllStates: 'Alle Zustände',
  SelectedStates: 'Ausgewählte Zustände',
  SelectStates: 'Zustände auswählen',
  PleaseSelectAtLeastOneState: 'Bitte wählen Sie mindestens einen Zustand aus',
  More: 'weitere',
  PaperSize: 'Papierformat',
  Orientation: 'Ausrichtung',
  Portrait: 'Hochformat',
  Landscape: 'Querformat',
  IncludeMetadata: 'Metadaten einschließen',
  IncludeTimestamp: 'Zeitstempel einschließen',
  ExportMode: 'Export-Modus',
  FullSVG: 'Gesamtes SVG',
  CurrentView: 'Aktueller Ausschnitt',
  NoZoomDetected: 'Kein Zoom erkannt - verwende gesamtes SVG',
  ShowEquipmentChanges: 'Änderungen der Betriebsmittel anzeigen',
  IncludeCoverPage: 'Titelblatt einschließen',
  Filename: 'Dateiname',
  Generate: 'Erstellen',
  Cancel: 'Abbrechen',

  // Report
  ZoomLevel,
  ReportSectionType,
  PreviewProgress: PreviewProgress,
  PageOrientations,
  PageMarginsMap,

  // Title block
  NetworkTitleBlock,
  NetworkTitleBlockLabel: 'Schriftfeld',
  NetworkTitleBlockDraft: 'Entwurf',
  NetworkTitleBlockSubtitle: 'seitenspezifisch',
  NetworkTitleBlockEdit: 'Schriftfeld bearbeiten',

  ReportNetworkTopologiesPagesShowPeriodDates: 'Datum der Topologie anzeigen',
  ReportNetworkEquipmentStatusShowEquipments: 'Betriebsmittel-Statusliste anzeigen',
  ReportNetworkTopologiesSelectTitle: 'Topologien auswählen',
  ReportNetworkTopologiesSelectAll: 'Alle auswählen',
  ReportNetworkSVGTopologiesPagesMismatch:
    'Seiten entsprechen nicht der Anzahl der Topologien. Seitennamen können nicht zugeordnet werden.',
  ReportSectionTopologiesSVGHint: 'Ändern sie die Ausrichtung um ein anderes layout zu erhalten',
  ReportSectionFrontMatterHint:
    'Das Inhaltsverzeichnis wird automatisch vor Export und nach Bearbeiten der Metadaten aktualisiert.',

  // Printing
  ReportPagePrintingInProgress: 'Druckvorgang bereits angestoßen. Bitte warten...',
  ReportPagePrintingError: 'Beim Erstellen des Berichts ist ein Fehler aufgetreten.',
  ReportPagePrintingStep1: 'Aktualisiere Inhaltsverzeichnis',
  ReportPagePrintingStep2: 'Schreibe Kopf- und Fußzeilen',
  ReportPagePrintingStep3: 'Setze Link-Referenzen im Inhaltsverzeichnis',
  ReportPagePrintingStep4: 'Schreibe Metadaten',
  ReportPagePrintingStep5: 'Bericht erstellt und wird heruntergeladen.',

  // Report Sections
  ReportPageSectionSelected: 'Ausgewählte Abschnitte',
  ReportPageSectionPDFSettings: 'Abschnitt PDF Darstellung',
  ReportPageSectionActions: 'Aktionen',
  ReportPageSectionAdd: 'Abschnitt hinzufügen',
  ReportPageSectionRename: 'Abschnitt umbenennen',
  ReportPageSectionDownloadPDF: 'Abschnitt-Vorschau PDF herunterladen',
  ReportPageSectionViewComponent: 'Abschnitt-Vorschau Komponente öffnen',
  ReportPageSectionShowPageNumber: 'Seitenzahl unten rechts anzeigen',
  ReportPageSectionShowPageNumberHint: 'Wird nur verwendet, wenn Fußzeile aktiviert ist',

  // Report Custom Section
  ReportPageCustomSectionAdd: 'Benutzerdefinierte Seite',
  ReportPageCustomSectionDescription: '.html, .svg, .txt, .md etc.',
  ReportPageCustomSectionNameDefault: 'Benutzerdefinierte Seite',
  ReportPageCustomSectionTitleLabel: 'Titel',
  ReportPageCustomSectionAdded: 'Seite "{name}" hinzugefügt',

  // Report Preview
  ReportPagePreviewPages: 'Seiten:',
  ReportPagePreviewZoom: 'Zoomstufe ',
  ReportPagePreviewEmptyTitle: 'Keine Abschnitte ausgewählt',
  ReportPagePreviewEmptyText: 'Fügen sie einen Abschnitt hinzu, um mit der Erstellung Ihres Berichts zu beginnen.',
  ReportPagePreviewOrientation: 'Ausrichtung',
  ReportPagePreviewSize: 'Seitengröße',
  ReportPagePreviewMargins: 'Seitenränder',
  ReportPagePreviewScaling: 'Skalierung',
  ReportPagePreviewError: 'Fehler beim Erstellen der Vorschau.',
  ReportPagePreviewErrorDetail: 'Schritt "{step}" ist fehlgeschlagen:',

  ReportGanttCoverConflicts: 'Konflikte',
  ReportGanttCoverLegend: 'Legende',

  // Topologies Matrix
  ReportPageTopologiesMatrixPeriodsPerPage: 'Topologie-Schritte pro Seite',
  ReportPageTopologiesMatrixWholeTable: 'Komplette Matrix',

  // Report Cover
  ReportCover: 'Deckblatt',
  ReportCoverSubject: 'Schaltungsplanung Report',
  ReportCoverLogoDialogTitle: 'Deckblatt-Logo bearbeiten',
  ReportCoverLogoDialogHeight: 'Logo höhe',
  ReportPageRename: '{page} umbenennen',

  // Report Changelog
  ReportChangelog: 'Änderungsverzeichnis',
  ReportChangelogShow: 'Änderungsverzeichnis anzeigen',
  ReportChangelogVersion: 'Version',
  ReportChangelogDate: 'Datum',
  ReportChangelogComment: 'Bemerkung',
  ReportChangelogAuthor: 'Autor',
  ReportChangelogAuditedBy: 'Geprüft',

  // Report TOC
  ReportTableOfContents: 'Inhalt',

  // Report Gantt Cover
  ReportGanttCoverRenderError: 'Das erforderliche zweite erneute Rendern der Titelseite ist fehlgeschlagen.',
  ReportGanttCoverTitle: 'Schaltungsdiagramm',
  ReportGanttCoverInfo: 'Überblick',

  ReportGanttCoverTimeframe: 'Zeitraum',
  ReportGanttCoverTopologies: 'Topologien',
  ReportGanttCoverEquipments: 'Betriebsmittel',
  ReportGanttCoverSwitchings: 'Schaltvorgänge',

  // Report Metadata Dialog
  ReportMetadataMain: 'Deckblatt & Metadaten',
  ReportMetadataHeaderFooter: 'Kopf- und Fußzeile',
  ReportMetadataTitle: 'Titel / Headline',
  ReportMetadataAuthor: 'Autor',
  ReportMetadataSubject: 'Betreff / Preline',
  ReportMetadataKeywords: 'Schlüsselwörter',
  ReportMetadataVersion: 'Version {version}',
  ReportMetadataVersionHint: 'Hinweis: Letzter Eintrag wird für das Deckblatt verwendet und gilt als aktuelle Version.',
  ReportMetadataKeywordsHint:
    'Schlüsselwörter helfen dabei, Ihr Dokument leichter auffindbar und besser durchsuchbar zu machen – sowohl für Suchmaschinen als auch in Dokumentenverwaltungen. Verwenden Sie aussagekräftige Schlagwörter als Best Practice, um die Organisation und Auffindbarkeit Ihrer PDF-Datei zu verbessern.',
  ReportMetadataCreatedAt: 'Erstellt am',
  ReportMetadataHeaderSection: 'Kopfzeile',
  ReportMetadataFooterSection: 'Fußzeile',
  ReportMetadataHeaderLeft: 'Kopfzeile links',
  ReportMetadataHeaderRight: 'Kopfzeile rechts',
  ReportMetadataHeaderCenter: 'Kopfzeile mitte',
  ReportMetadataFooterLeft: 'Fußzeile links',
  ReportMetadataFooterRight: 'Fußzeile rechts',
  ReportMetadataFooterCenter: 'Fußzeile mitte',
  ReportMetadataFooterLeftHint: 'Leer lassen für Abschnittstitel',
  ReportMetadataFooterRightHint: 'Seitenzahl wird automatisch hinzugefügt',
  ReportMetadataRestoreDefault: 'Standardwert wiederherstellen',
  ReportMetadataButton: 'Metadaten bearbeiten',
  ReportErrorGotenberg503:
    'PDF-Konvertierung durch Server abgebrochen (Timeout nach {seconds}s). Die zu konvertierende Datei ist wahrscheinlich zu groß.',

  // Network Map report
  EquipmentDiffToggle: 'Betriebsmittel mit Änderungen',
  EquipmentDiffCaption: 'Geschaltete Betriebsmittel',
  EquipmentDiffColor: 'Darstellung',
  EquipmentDiffEquipment: 'Betriebsmittel',
  EquipmentDiffProject: 'Projekt',
  EquipmentDiffCategory: 'Kategorie',
  EquipmentDiffVoltage: 'Spannungsebene',
  EquipmentDiffNone: 'Keine Betriebsmittel geschaltet.',
  TopologyChangesGrouping: 'Gruppierung Topologie-Änderungen',
  TopologyChangesGroupingMode,

  // Projects
  ProjectDialogEditTitle: 'Projekt bearbeiten',

  // SubProjects
  SubProjectDialogEditTitle: 'Teilprojekt bearbeiten',

  // Equipment
  EquipmentDialogEditTitle: 'Betriebsmittel bearbeiten',
  EquipmentDialogCreateTitle: 'Betriebsmittel erstellen',
  EquipmentsOnlyDB: 'Betriebsmittel aus SVG die noch nicht zugewiesen sind',
  EquipmentTableColSelect: 'Spalten auswählen',
  EquipmentTableFooterPaginator: 'Einträge pro Seite',
  EquipmentTableFooterPaginatorOf: 'von',
  EquipmentTableNoData: 'Keine Betriebsmittel gefunden',
  EquipmentTableNoDataDescription: 'Es wurden keine Betriebsmittel für diese Aktivität gefunden.',
  EquipmentColumnsExtra,
  // Equipment status translations
  EquipmentStatus,
  EquipmentStatusHint,
} as const
